import { create } from "zustand";
import {
  persist,
  createJSONStorage,
  StateStorage,
  devtools,
} from "zustand/middleware";
import { useCallback } from "react";
import { useStorageSync } from "hooks/useSyncTabsEffect";

interface EagleEyeNetworkState {
  // State
  isAuthenticated: boolean;

  // Actions
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  clearAuthData: () => void;
}

// Create a custom storage object
const customStorage: StateStorage = {
  getItem: (name) => {
    const str = localStorage.getItem(name);
    return str;
  },
  setItem: (name, value) => localStorage.setItem(name, value),
  removeItem: (name) => localStorage.removeItem(name),
};

// Storage key for localStorage events
const STORAGE_KEY = "eagle-eye-network-auth";

// DevTools configuration options
const devToolsOptions = {
  name: "EagleEyeNetwork",
  enabled: true,
  anonymousActionType: "UNKNOWN_ACTION",
  serialize: {
    options: true,
  },
};

// Create the store with middleware chain
export const useEagleEyeNetworkStore = create<EagleEyeNetworkState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        isAuthenticated: false,

        // Actions
        setIsAuthenticated: (isAuthenticated) =>
          set(
            { isAuthenticated },
            false,
            { type: "eagle-eye-network/setIsAuthenticated", payload: isAuthenticated }
          ),

        clearAuthData: () =>
          set({ isAuthenticated: false }, false, "eagle-eye-network/clearAuthData"),
      }),
      {
        name: STORAGE_KEY,
        partialize: (state) => ({ isAuthenticated: state.isAuthenticated }),
        storage: createJSONStorage(() => customStorage),
      }
    ),
    devToolsOptions
  )
);

/**
 * Hook for synchronizing auth state between browser tabs
 * This ensures that login/logout in one tab is reflected in all other tabs
 */
export function useSyncTabsEffect() {
  const setIsAuthenticated = useEagleEyeNetworkStore((state) => state.setIsAuthenticated);
  const clearAuthData = useEagleEyeNetworkStore((state) => state.clearAuthData);

  const handleChange = useCallback(
    (newValue: string | null) => {
      if (!newValue) {
        // Auth data was cleared in another tab
        clearAuthData();
        return;
      }
      try {
        const newData = JSON.parse(newValue);
        if (newData?.state) {
          setIsAuthenticated(!!newData.state.isAuthenticated);
        }
      } catch (error) {
        console.error("Error synchronizing tabs:", error);
      }
    },
    [setIsAuthenticated, clearAuthData]
  );

  useStorageSync(STORAGE_KEY, handleChange);
}
